import { Sun, Moon } from 'lucide-react';
import { useThemeStore } from '@stores/themeStore';

interface ThemeToggleProps {
  showLabel?: boolean;
  className?: string;
  /** Icon size classes; the nav rail footer passes `w-4 h-4` to match Sign out. */
  iconClassName?: string;
}

export function ThemeToggle({ showLabel = false, className = '', iconClassName = 'w-5 h-5' }: ThemeToggleProps) {
  // Driven by the RESOLVED theme, never the raw preference. 'system' is still a
  // valid stored preference (and the default for a first-time visitor), but this
  // is a two-state day / night switch: showing a third "system" monitor icon
  // told the user nothing they could act on. What they want to see is which
  // mode they are actually looking at, which is what resolvedTheme holds.
  const { resolvedTheme, toggleTheme } = useThemeStore();
  const isDark = resolvedTheme === 'dark';

  return (
    <button
      onClick={toggleTheme}
      className={`p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-800 transition-colors ${className}`}
      title={`Switch to ${isDark ? 'light' : 'dark'} mode`}
      aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
    >
      {/* The icon is the action, matching the tooltip: a moon offers dark
          mode, a sun offers light mode. */}
      {isDark ? <Sun className={iconClassName} /> : <Moon className={iconClassName} />}
      {showLabel && <span className="ml-2 text-sm">{isDark ? 'Dark' : 'Light'}</span>}
    </button>
  );
}

export function ThemeSelector() {
  const { theme, setTheme } = useThemeStore();

  // Day / night only, matching ThemeToggle. 'system' remains a legal stored
  // value (it is the default until the user picks), it is just not offered as a
  // choice any more.
  const options = [
    { value: 'light' as const, label: 'Light', icon: Sun },
    { value: 'dark' as const, label: 'Dark', icon: Moon },
  ];

  return (
    <div className="flex items-center gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-lg">
      {options.map((option) => (
        <button
          key={option.value}
          onClick={() => setTheme(option.value)}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors ${
            theme === option.value
              ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
          }`}
        >
          <option.icon className="w-4 h-4" />
          <span>{option.label}</span>
        </button>
      ))}
    </div>
  );
}
